// Token manager — the in-memory access token, silent refresh, cross-tab hand-off,
// createAuthFetch.
//
// Moved here from cloud/dp/shell/src/auth/token-manager.ts so the shell,
// dev-shell fixtures, and future apps all share one JWT-lifecycle
// implementation. Browser-only (fetch, `BroadcastChannel`, and one `document.cookie`
// deletion in `clearTokens`); no cookie value is ever written — core-api owns
// `zion_access_token` and `zion_refresh_token` server-side (#2590, #2967). Not safe
// to import from a Node context.
//
// Feature 2026-09-21-dp-session-cookie: the access token lives in `sessionStore`
// (module memory, D2) and the refresh token lives in an HttpOnly cookie the browser
// attaches to `/v1/auth` by itself (D9). The only Web Storage this package still
// touches is `legacy-storage.ts`, which drains a pre-cookie release's keys once per
// boot (D11, R-10).
import { sessionResponseSchema } from '@zion/sdk-contracts';
import { sessionStore } from './session-store.js';
import { LEGACY_ACCESS_TOKEN_KEY, clearLegacyRefreshToken, getLegacyRefreshToken, migrateLegacyStorage, reloadLegacyRefreshToken, storeLegacyRefreshToken, } from './legacy-storage.js';
import { openSessionChannel } from './session-channel.js';
import { computeExpiresAt, extractClaims, isTokenExpired } from './token-utils.js';
import { callerSignalOf, computeRefreshDelay, classifyResponse, computeBackoffDelay, sessionTransportHeaders, startWait, REFRESH_BUFFER_MS, INITIAL_BACKOFF_SEC, } from './token-manager-utils.js';
import { coldStartBackoffMs, isColdStartRetryable } from '../transport/cold-start.js';
/** Refresh failed for a reason that means the session is genuinely over
 *  (server rejected the refresh token: HTTP 401/403). Callers MUST log out. */
export class TerminalAuthError extends Error {
    status;
    token;
    /**
     * @param token The access token whose pair this verdict is about, or null when
     *   there was none to refresh with. `AuthProvider` ends only that session:
     *   a verdict about an old pair must not end a session that a login stored
     *   between the verdict and the session end (#2680).
     */
    constructor(status, token = null) {
        super(`Refresh rejected: ${status}`);
        this.status = status;
        this.token = token;
        this.name = 'TerminalAuthError';
    }
}
/** Refresh failed transiently (network error, HTTP 5xx/429). The session is
 *  still valid; callers MUST retry, NOT log out. */
export class TransientRefreshError extends Error {
    status;
    constructor(status) {
        super(`Refresh temporarily failed: ${status}`);
        this.status = status;
        this.name = 'TransientRefreshError';
    }
}
// ── The session this tab holds ───────────────────────────────────────────────
/** The access token this tab holds, or `null`. The public façade over `sessionStore.get()`; the
 *  shell's `api/client.ts` and `mockup/main.tsx` keep importing it by this name. Throws nothing. */
export function getAccessToken() {
    return sessionStore.get();
}
/**
 * Hold a freshly minted session and tell the other tabs: the one writer of a session into this tab
 * and the one poster of `token-refreshed`. Callers: `doRefresh` after a rotation,
 * `AuthProvider.login` after a mint.
 * @param session either member of `SessionResponse`, told apart by `'refreshToken' in session`. A
 *   `CookieSessionResponse` means core-api set the cookie, so `clearLegacyRefreshToken()` runs and
 *   this tab is on the cookie arm for good. A `TokenPairResponse` means an N-1 core-api pod
 *   answered, so `storeLegacyRefreshToken(session.refreshToken)` runs and the tab stays on the
 *   body arm (R-10). Both members are `.strict()`, so no third shape reaches here.
 * @returns nothing. `doRefresh` resolves `session.accessToken`, which it already holds.
 *
 * Side effects: `sessionStore.set(session.accessToken)`, the legacy-key write or delete, then one
 * `token-refreshed` post carrying the access token alone. A tab that receives that message writes
 * its own store directly and posts nothing, so no echo loop exists. It throws whatever
 * `sessionStore.set` throws for a non-JWT.
 */
export function adoptSession(session) {
    // The store first. `sessionStore.set` throws on a body whose `accessToken` is not a decodable
    // JWT, and it computes the expiry before it writes either variable, so that throw leaves the
    // held session exactly as it was and no legacy key is written for a session this tab never got.
    sessionStore.set(session.accessToken);
    if ('refreshToken' in session) {
        // An N-1 core-api pod answered: it rotated the family and set no cookie, so the rotated token
        // is this tab's only credential and has to survive a reload (R-10, flow 4.2 step 4).
        storeLegacyRefreshToken(session.refreshToken);
    }
    else {
        // core-api put the refresh token in the cookie, so this tab is on the cookie arm for good.
        // Keeping the key would send a spent token in the body on every later refresh.
        clearLegacyRefreshToken();
    }
    sessionChannel().post({ type: 'token-refreshed', accessToken: session.accessToken });
}
/**
 * End the local session: clear the store and post `signed-out`. The caller owns the timers. Throws
 * nothing; idempotent. It also deletes a legacy script-written `zion_access_token` cookie with an
 * empty value and `Path=/`, the only cookie script can reach, because core-api's is HttpOnly. That
 * write predates this feature and stays.
 *
 * ⚠️ On the logout path the server's asset cookie no longer survives: under cookie transport
 * core-api clears both `zion_refresh_token` and `zion_access_token` (D7, chain C4 step 1). It does
 * survive, for up to the asset token's own 900-second TTL, where no logout request reaches the
 * server — the terminal `hydrateTokens` path, where this runs and no request follows.
 *
 * The three `localStorage.removeItem` calls the pre-cookie version made are gone. One
 * `clearLegacyRefreshToken()` takes their place, so a revoked or refused legacy token is not left
 * readable in a tab that an N-1 pod had put back on the body arm (R-15).
 *
 * The cookie it deletes is named `zion_access_token` with `Path=/`, which is what the old
 * script-side write used; deletion needs a name, domain and path match.
 */
export function clearTokens() {
    sessionStore.clear();
    clearLegacyRefreshToken();
    // ⛔ DELETION ONLY — an empty value, so no credential is written. It removes a LEGACY
    // `zion_access_token` an older SDK wrote from script, the only cookie script can reach:
    // core-api's own is `HttpOnly` and script cannot overwrite that name. `Path=/` matches the old
    // write, because deletion needs a name, domain and path match.
    document.cookie = `${LEGACY_ACCESS_TOKEN_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
    sessionChannel().post({ type: 'signed-out' });
}
/**
 * Upper bound on the refresh request. `doRefresh` awaits it while holding the
 * refresh mutex, so a request that stalls without rejecting would hold that
 * mutex forever: every later refresh queues behind it, and so does
 * `AuthProvider`'s session end, which would leave the user locally signed in
 * until a reload (Copilot, PR #2625). An abort throws, and the catch below
 * classifies it `TransientRefreshError`, which is the honest verdict: the
 * session is kept and `runScheduledRefresh` retries with backoff.
 */
export const REFRESH_REQUEST_TIMEOUT_MS = 15_000;
/**
 * Post `/v1/auth/refresh` and parse whichever session body comes back.
 *
 * The request carries `Content-Type: application/json`, `sessionTransportHeaders()` and
 * `AbortSignal.timeout(REFRESH_REQUEST_TIMEOUT_MS)`. The body is `{}`, or
 * `{ refreshToken: legacyRefreshToken }` while this tab still holds a migrated token; the shape is
 * `refreshRequestSchema`. It passes no `credentials` option (default F7): the request is
 * same-origin, so the browser attaches the cookie under its `Path=/v1/auth` by itself.
 *
 * @param legacyRefreshToken the token `migrateLegacyStorage` drained from `localStorage`, or the
 *   one an N-1 pod rotated back into it, or `null` when this tab is on the cookie arm.
 * @returns the parsed body, either member of `SessionResponse` (R-10). A `CookieSessionResponse`
 *   is how the client proves core-api stripped `refreshToken` (chain C1 step 2). A
 *   `TokenPairResponse` is how it learns an N-1 core-api pod answered.
 * @throws `TransientRefreshError('network')` when the fetch throws or the timeout fires;
 *   `TerminalAuthError(401 | 403)` when the server refused the credential;
 *   `TransientRefreshError(status)` for any other non-2xx; and `TransientRefreshError(res.status)`
 *   for a 2xx whose body parses as neither member (R-5). A 200 says the server did not refute the
 *   session, so a schema failure refuses the body and keeps the session: signing every user out
 *   for the length of a half-rolled deployment is the wrong answer. Both members are `.strict()`,
 *   so a body with an unknown key fails both arms.
 */
async function refreshTokens(legacyRefreshToken) {
    let res;
    try {
        res = await fetch('/v1/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...sessionTransportHeaders() },
            body: JSON.stringify(legacyRefreshToken === null ? {} : { refreshToken: legacyRefreshToken }),
            signal: AbortSignal.timeout(REFRESH_REQUEST_TIMEOUT_MS),
        });
    }
    catch {
        throw new TransientRefreshError('network');
    }
    if (res.status === 401 || res.status === 403)
        throw new TerminalAuthError(res.status);
    if (!res.ok)
        throw new TransientRefreshError(res.status);
    // ⛔ `res.json()` REJECTS ON AN EMPTY OR TRUNCATED 200, AND THAT IS TRANSIENT (R-42). A proxy or
    // a rolling pod can answer 200 with no body at all; `res.json()` then throws a `SyntaxError`
    // before `safeParse` runs, and an unclassified throw out of here reads as terminal at
    // `hydrateTokens`, which clears a session the server never refuted.
    let body;
    try {
        body = await res.json();
    }
    catch {
        throw new TransientRefreshError(res.status);
    }
    const parsed = sessionResponseSchema.safeParse(body);
    // A 200 says the server did not refute the session, so a body neither union member accepts
    // refuses the body and keeps the session (R-5).
    if (!parsed.success)
        throw new TransientRefreshError(res.status);
    return parsed.data;
}
// ── Cross-tab lock ─────────────────────────────────────────────────────────
const REFRESH_LOCK_NAME = 'zion-auth-refresh';
/** The in-tab queue behind `withRefreshLock` when the Web Locks API is absent:
 *  each holder runs after the previous one settles, success or failure. */
let inTabLockTail = Promise.resolve();
/** Run `fn` under a mutex: the cross-tab Web Lock when the API is available,
 *  otherwise an in-tab queue. `refreshInFlight` coalesces concurrent refreshes
 *  either way; the mutex is what keeps a refresh and a session end apart.
 *
 *  Exported for `AuthProvider`'s session end. The DP's `/v1/auth/logout`
 *  revokes the whole refresh-token FAMILY, so a refresh that rotates the pair
 *  while the logout request is in flight is revoked with it, and a logout that
 *  then spares the rotated pair leaves a stored session the server has already
 *  ended (Copilot, PR #2625). Under this lock the two cannot interleave: a
 *  refresh that is running finishes first, and one that starts after finds no
 *  refresh token and is terminal. The queue fallback closes the same window in
 *  one tab; only a refresh in ANOTHER tab can still overlap a logout here when
 *  the API is absent, and that tab's next refresh with the revoked pair is
 *  terminal. */
export async function withRefreshLock(fn) {
    if (navigator.locks?.request)
        return navigator.locks.request(REFRESH_LOCK_NAME, fn);
    const run = inTabLockTail.then(() => fn());
    inTabLockTail = run.then(() => undefined, () => undefined);
    return run;
}
// ── Coalesced refresh ────────────────────────────────────────────────────────
/** The one open refresh this tab shares with every caller that joins it while it is in flight. */
let refreshInFlight = null;
/**
 * The access token the last `doRefresh` actually wrote into the store, or `null`.
 *
 * ⛔ WRITTEN ONLY WHERE `adoptSession` RUNS (R-45). `doRefresh` resolves a token on four paths
 * and stores one on exactly one of them: the other three hand back a token a sibling tab or a
 * login had already put there. `runScheduledRefresh`'s stale-generation branch uses this to clear
 * only what its own refresh stored, so a login that landed while the retry was in flight
 * survives. Comparing the store against the resolved token cannot do it: the currency guard
 * returns the login's own token, so the store equals it in the very case that must survive.
 */
let lastRefreshStoredToken = null;
/**
 * Refresh the access token once, under the refresh mutex, coalescing concurrent callers.
 *
 * It takes no parameter (R-10, finding A-4). Inside the lock it re-reads `sessionStore.get()` and
 * `getLegacyRefreshToken()`, and passes the second to `refreshTokens`. The migrated token
 * therefore lives in module memory rather than in an argument, and whichever refresh wins the
 * coalescing carries it: a caller that joins an in-flight refresh cannot send `{}` over a token
 * the tab still holds.
 *
 * The guard `if (!current) throw new TerminalAuthError(401, null)` that the pre-cookie version ran
 * is gone. An empty store is the normal cold-boot state now; before this feature it was the
 * signature of a partly cleared logout, and `endSession` runs the whole revoke-and-clear under
 * this same lock, so the window that guard closed does not reopen.
 *
 * @returns the access token this tab should use. When the store changed since entry and the held
 *   token is not within `REFRESH_BUFFER_MS` of expiry, it returns that token and sends no request:
 *   that is how a tab skips a refresh another tab already did. Otherwise it returns the token
 *   `adoptSession` has just taken from the parsed body.
 * @throws `TerminalAuthError` when the server refused the credential, or when the store was
 *   cleared while the request was in flight (a logout won, so a successful pair must not
 *   resurrect the session). `TransientRefreshError` for a network failure, a 5xx, a 429 or a 200
 *   whose body parses as neither union member.
 */
function doRefresh() {
    if (refreshInFlight)
        return refreshInFlight;
    const tokenAtEntry = sessionStore.get();
    refreshInFlight = withRefreshLock(async () => {
        // Another tab may have refreshed while we waited for the lock. If the held token changed and
        // is not (near-)expired, use it — do NOT spend the refresh cookie the sibling just rotated.
        const current = sessionStore.get();
        if (current &&
            current !== tokenAtEntry &&
            !isTokenExpired(sessionStore.getExpiresAt(), REFRESH_BUFFER_MS)) {
            return current;
        }
        const tokenBeforeFetch = current;
        // The held session can change while `/refresh` is in flight, and the verdict that comes back
        // is about the OLD pair either way (Copilot, PR #2625). A login or another tab replaced the
        // session: adopt it. On success that means dropping a pair minted for the old session instead
        // of overwriting the new one; on a 401/403 it means the stale rejection must not clear or log
        // out a session it never judged. A logout cleared the session: holding a successful pair
        // would re-establish a session the user ended and the still-registered callback would run
        // identity recovery on it, so that is terminal.
        let session;
        try {
            // Read inside the lock, never as an argument: whichever refresh wins the coalescing carries
            // the migrated token, and a caller that joins an in-flight refresh cannot send `{}` over a
            // token the tab still holds (R-10, finding A-4).
            session = await refreshTokens(getLegacyRefreshToken());
        }
        catch (err) {
            const tokenAfterFetch = sessionStore.get();
            if (tokenAfterFetch !== tokenBeforeFetch && tokenAfterFetch !== null)
                return tokenAfterFetch;
            // The verdict names the pair it judged, so the provider can end that session and no other
            // (#2680). Two states reach this line: the pair unchanged, and the pair cleared during the
            // fetch. Name null for the second, as the success path does below: a verdict naming a pair
            // that is no longer held would match nothing, and the provider would keep rendering a
            // session the server rejected.
            if (err instanceof TerminalAuthError) {
                throw new TerminalAuthError(err.status, tokenAfterFetch === null ? null : tokenBeforeFetch);
            }
            throw err;
        }
        const tokenAfterFetch = sessionStore.get();
        if (tokenAfterFetch !== tokenBeforeFetch) {
            if (tokenAfterFetch === null)
                throw new TerminalAuthError(401, null);
            return tokenAfterFetch;
        }
        adoptSession(session);
        // The one path that writes the store, so the one path that records (R-45).
        lastRefreshStoredToken = session.accessToken;
        return session.accessToken;
    }).finally(() => { refreshInFlight = null; });
    return refreshInFlight;
}
// ── Silent refresh scheduler ─────────────────────────────────────────────────
let refreshTimer = null;
let refreshBackoffSec = 0; // 0 = not currently backing off
/**
 * Which round of scheduling a refresh belongs to. `stopSilentRefresh` advances it (R-43).
 *
 * ⛔ CLEARING A TIMER CANNOT RECALL A REQUEST. `REFRESH_REQUEST_TIMEOUT_MS` is 15 s and
 * `BOOT_RETRY_GRACE_MS` is 15 s, so the boot retry can still be in flight when the grace fires,
 * settles the provider signed out and calls `stopSilentRefresh`. Its verdict then lands under a
 * login page. A caller that passes the generation it was scheduled in lets `runScheduledRefresh`
 * see that it is stale and drop the result instead of signing the user back in behind the form.
 */
let refreshGeneration = 0;
/** The session a failed refresh was about: the judged pair for a terminal
 *  verdict, otherwise whatever is stored now (an unexpected failure is a
 *  verdict about the current session, the one that would have been refreshed). */
function expiredSessionOf(err) {
    return err instanceof TerminalAuthError ? err.token : getAccessToken();
}
let onTokenRefreshed = null;
let onSessionExpired = null;
export function setRefreshCallbacks(onRefreshed, onExpired) {
    onTokenRefreshed = onRefreshed;
    onSessionExpired = onExpired;
}
// Refresh now, then decide what's next — the ONE place that maps a refresh
// outcome → (re-arm proactive | backoff retry | logout).
//
// `generation` is the round this run was scheduled in, or `undefined` for the proactive and
// visibility paths, which have no hold to outlive (R-43).
async function runScheduledRefresh(generation) {
    try {
        const newToken = await doRefresh();
        // ⛔ A VERDICT FROM A ROUND THAT IS OVER IS DISCARDED, INCLUDING ITS TOKEN. The boot retry
        // and the grace share a 15-second bound, so this resolve can arrive after the provider has
        // settled signed out. `doRefresh` has already written the store through `adoptSession`, so
        // dropping the callback alone would leave a live token behind a login page (R-43).
        //
        // ⛔ AND IT CLEARS ONLY WHAT ITS OWN REFRESH STORED (R-45). A login that lands while the
        // retry is in flight makes `doRefresh`'s currency guard hand back the LOGIN's token without
        // storing anything, and the provider is already rendering it. An unconditional clear wipes
        // that session while the provider still points at it.
        if (generation !== undefined && generation !== refreshGeneration) {
            if (sessionStore.get() === lastRefreshStoredToken)
                sessionStore.clear();
            return;
        }
        refreshBackoffSec = 0; // reset on success
        onTokenRefreshed?.(newToken);
        scheduleRefresh(); // re-arm proactive timer from the NEW expiry
    }
    catch (err) {
        if (generation !== undefined && generation !== refreshGeneration)
            return;
        if (err instanceof TransientRefreshError) {
            // Keep the session; retry after backoff. First retry = INITIAL_BACKOFF_SEC
            // (5s), then double via computeBackoffDelay (cap 120s).
            refreshBackoffSec =
                refreshBackoffSec === 0 ? INITIAL_BACKOFF_SEC : computeBackoffDelay(refreshBackoffSec);
            if (refreshTimer !== null)
                clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => { void runScheduledRefresh(generation); }, refreshBackoffSec * 1000);
            return;
        }
        onSessionExpired?.(expiredSessionOf(err)); // TerminalAuthError (or any non-transient) ⇒ end that session
    }
}
function scheduleRefresh() {
    if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    const msUntilRefresh = computeRefreshDelay(sessionStore.getExpiresAt(), Date.now());
    if (msUntilRefresh === null)
        return; // past buffer — interceptor / visibility handles it
    refreshTimer = setTimeout(() => { void runScheduledRefresh(); }, msUntilRefresh);
}
/** Start the silent refresh timer. Call after login or hydration. */
export function startSilentRefresh() {
    refreshBackoffSec = 0;
    scheduleRefresh();
}
/**
 * Arm the backoff retry for a refresh that failed transiently outside the scheduler.
 *
 * The one caller is `AuthProvider`'s mount effect, on a `TransientRefreshError` out of
 * `hydrateTokens` (R-30). That failure leaves the store empty, and `scheduleRefresh` arms nothing
 * from a null expiry, so without this the tab renders signed out after one network blip, one 429
 * or one 5xx from a rolling core-api, and nothing tries again until a reload or a tab focus.
 *
 * It sets the same first delay `runScheduledRefresh`'s own transient arm sets,
 * `INITIAL_BACKOFF_SEC`; the doubling from there is that function's, so the sequence is 5 s, then
 * 10 s, then 20 s to the `MAX_BACKOFF_DELAY_SEC` cap. A success invokes `onTokenRefreshed`, which
 * is how the provider learns the session came back. Throws nothing; calling it twice replaces the
 * pending retry rather than adding one.
 */
export function startRefreshRetry() {
    if (refreshTimer !== null)
        clearTimeout(refreshTimer);
    refreshBackoffSec = INITIAL_BACKOFF_SEC;
    const generation = refreshGeneration;
    refreshTimer = setTimeout(() => { void runScheduledRefresh(generation); }, refreshBackoffSec * 1000);
}
/** Stop the silent refresh timer. Call on logout. */
export function stopSilentRefresh() {
    if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    // Every caller of this means "nothing this tab scheduled should land any more", and a request
    // already in flight cannot be recalled by clearing a timer. Advancing the generation is how a
    // verdict that arrives afterwards is discarded (R-43).
    refreshGeneration += 1;
}
// ── Cross-tab hand-off ─────────────────────────────────────────────
/**
 * This tab's one `SessionChannel`, opened on first use and `null` while none is open.
 *
 * ⛔ ONE INSTANCE FOR POSTING AND FOR LISTENING. `adoptSession` and `clearTokens` post on the same
 * channel `startSessionSync` subscribes to, and a `BroadcastChannel` never delivers to the
 * instance that posted. That is what keeps a tab from hearing its own refresh and its own
 * sign-out; two instances would make this tab's handler fire on its own writes and echo.
 */
let sessionChannel_ = null;
/** The unsubscribe `SessionChannel.subscribe` returned, or `null` while nothing is subscribed. */
let sessionSyncUnsubscribe = null;
/**
 * Does `accessToken` decode to a JWT this tab can derive an expiry from?
 *
 * `sessionStore.set` calls `computeExpiresAt`, which throws `Invalid JWT format` on a string that
 * is not three dot-separated parts and returns `NaN` for a payload with no `exp`. Both reach the
 * store from a peer's message, where a throw is an uncaught error in an event handler, so the
 * `token-refreshed` arm asks this first and drops the message on a `false` (R-46).
 */
function hasDecodableExpiry(accessToken) {
    try {
        return Number.isFinite(computeExpiresAt(accessToken));
    }
    catch {
        return false;
    }
}
/** The open channel, opening one if this tab has none. */
function sessionChannel() {
    sessionChannel_ ??= openSessionChannel();
    return sessionChannel_;
}
/**
 * Listen for the other tabs. Replaces `startCrossTabSync` with the same callback shape, so
 * `AuthProvider`'s handler does not move. On `token-refreshed` it drops a token it cannot derive
 * an expiry from (R-46), re-reads the legacy refresh key another tab may have rotated (R-47),
 * writes `sessionStore.set(accessToken)`, calls `onSync(accessToken)` and re-arms the proactive
 * timer from the new expiry. On `signed-out` it clears the store and the migrated legacy token, stops the
 * refresh timers and the visibility driver, then calls `onSync(null)` (R-44). Both arms write the
 * store directly rather than through `adoptSession` or `clearTokens`, so a receiving tab posts
 * nothing back. Calling it twice replaces the subscription. Throws nothing.
 *
 * @param onSync the provider's handler: the fresh access token on a refresh in another tab, `null`
 *   on a sign-out in another tab.
 */
export function startSessionSync(onSync) {
    sessionSyncUnsubscribe?.();
    sessionSyncUnsubscribe = sessionChannel().subscribe((message) => {
        if (message.type === 'token-refreshed') {
            // ⛔ A PEER'S TOKEN IS STILL UNTRUSTED INPUT (R-46). `isSessionChannelMessage` proved the
            // shape; this proves the value. `sessionStore.set` derives the expiry with
            // `computeExpiresAt`, which throws on a string that is not a JWT and yields `NaN` for one
            // with no `exp`, and a throw here is an uncaught error inside a message handler.
            if (!hasDecodableExpiry(message.accessToken))
                return;
            // ⛔ RE-READ THE LEGACY KEY BEFORE ADOPTING (R-47). Two body-arm tabs share that key: the
            // sender's rotation wrote a new token into it, and this tab's module variable still holds
            // the one that rotated away. Presenting that on the next refresh is a reuse, and a reuse
            // past the 60-second grace kills the family for every tab. An absent key means the sender
            // is on the cookie arm now, so this tab's body-arm token is spent.
            reloadLegacyRefreshToken();
            // Write the store directly rather than through `adoptSession`, so a receiving tab posts
            // nothing back and the two tabs cannot echo each other.
            sessionStore.set(message.accessToken);
            onSync(message.accessToken);
            scheduleRefresh();
            return;
        }
        // ⛔ THE RECEIVER ENDS ITS OWN SESSION THE WAY A LOCAL LOGOUT DOES (R-44). The sender's
        // `clearTokens()` reaches this tab's store only through this message; it cannot reach this
        // tab's module variables or its timers. A body-arm tab left holding `getLegacyRefreshToken()`
        // keeps a revoked token in memory with a live driver that will spend it, and after a
        // transient logout failure the cookie family is still live for that driver to renew.
        //
        // `stopSilentRefresh()` clears the proactive and retry timers and advances the retry
        // generation, so a refresh already in flight here is discarded rather than adopted;
        // `stopVisibilityRefresh()` removes the driver that would start a new one on the next focus.
        // It still posts nothing back: the sender told every tab already (S26).
        sessionStore.clear();
        clearLegacyRefreshToken();
        stopSilentRefresh();
        stopVisibilityRefresh();
        onSync(null);
    });
}
/** Unsubscribe and close the channel. Idempotent. Replaces `stopCrossTabSync`. */
export function stopSessionSync() {
    sessionSyncUnsubscribe?.();
    sessionSyncUnsubscribe = null;
    sessionChannel_?.close();
    sessionChannel_ = null;
}
// ── Hydration ─────────────────────────────────────────────────────
/**
 * Obtain an access token at boot, through the refresh cookie. It runs `migrateLegacyStorage()`
 * first, then one `doRefresh()`, which reads the migrated token inside the lock. Unlike the
 * removed version it always calls the server: this tab's memory is empty on every page load (D9).
 * @returns the fresh access token, or `null` when the server refused the credential. A 401 or 403
 *   is not thrown on: it resolves `null` after `clearTokens()` has run, which is the signed-out
 *   verdict `AuthProvider` acts on.
 * @throws `TransientRefreshError` when the refresh failed for a reason that is not a refusal:
 *   network, 5xx, 429, or a 200 whose body does not parse. Nothing here retries it. The caller
 *   owns that, and `AuthProvider`'s mount effect is the caller. It runs `startRefreshRetry()`,
 *   which arms ONE retry at `INITIAL_BACKOFF_SEC` with the store empty, and holds `loading` until
 *   that retry's verdict or `BOOT_RETRY_GRACE_MS`, whichever comes first (R-38). On the bound it
 *   settles signed out and clears the pending retry, so no token lands behind a login page. A
 *   token another tab broadcasts during the hold ends it too. A tab whose cookie session is live
 *   must not render the login page after one 5xx.
 */
export async function hydrateTokens() {
    // Before any network call (chain C1 step 4): a tab that closes mid-request must not leave a
    // readable refresh token behind, and the token survives in module memory so a transient failure
    // retries with it (R-10).
    migrateLegacyStorage();
    // The store as it was when this boot began. The terminal arm below clears only this session
    // (R-31). `AuthProvider` wraps the router, so a mint route's `login()` runs concurrently with
    // the boot refresh, and on an anonymous boot the terminal arm runs every time. An
    // unconditional `clearTokens()` wipes a session that completed between `doRefresh`'s throw and
    // this line, posts `signed-out` to the other tabs, and leaves the user who just finished an
    // OAuth exchange signed out. `doRefresh`'s own currency guard covers only a login that lands
    // before it reads the store.
    const sessionAtEntry = sessionStore.get();
    try {
        return await doRefresh();
    }
    catch (err) {
        if (err instanceof TransientRefreshError)
            throw err; // caller keeps whatever session it has
        if (sessionStore.get() === sessionAtEntry)
            clearTokens();
        return null;
    }
}
/**
 * Refresh now, outside the scheduler. The token-manager side of `useAuth().refreshSession()`, and
 * `AuthProvider` is its only caller. It is `doRefresh()` and inherits the coalescing: a call made
 * while a refresh is in flight resolves with that refresh's result and sends no second request.
 * @returns the fresh access token.
 * @throws `TerminalAuthError` when the server refused the credential, `TransientRefreshError`
 *   otherwise.
 */
export async function refreshAccessToken() {
    return doRefresh();
}
/**
 * Refresh now and tell the mounted `AuthProvider`, as the scheduler does. The default refresh of
 * the kernel step-up wrapper after an approval (kernel apps, WS-17): `createStepUpFetch` step 8
 * and `StepUpProvider`. The package barrels do not export it.
 *
 * It awaits `doRefresh()` and inherits its coalescing.
 * - A success calls the `onTokenRefreshed` callback of `setRefreshCallbacks` with the new token.
 *   `AuthProvider` then sets its `token` state, re-arms the proactive timer from the new expiry
 *   and recovers the identity, so `useKernelSession` reads the new `stepUpAt`.
 * - A `TransientRefreshError` rejects with that error and calls no callback.
 * - Any other failure, `TerminalAuthError` included, calls `onSessionExpired` with the judged
 *   session, as the 401 path of `createAuthFetch` does, and rejects with the same error.
 *
 * With no callbacks registered (no `AuthProvider` ever mounted), it is `refreshAccessToken`.
 * @returns the fresh access token.
 */
export async function refreshAndNotify() {
    let newToken;
    try {
        newToken = await doRefresh();
    }
    catch (err) {
        if (!(err instanceof TransientRefreshError))
            onSessionExpired?.(expiredSessionOf(err));
        throw err;
    }
    onTokenRefreshed?.(newToken);
    return newToken;
}
// ── Tab reactivation ─────────────────────────────────────────────────────────
let visibilityListener = null;
export function startVisibilityRefresh() {
    stopVisibilityRefresh();
    visibilityListener = () => {
        if (document.visibilityState !== 'visible')
            return;
        if (isTokenExpired(sessionStore.getExpiresAt(), REFRESH_BUFFER_MS)) {
            void runScheduledRefresh(); // expired ⇒ refresh now (transient-safe; terminal ⇒ logout)
        }
        else {
            scheduleRefresh(); // not expired ⇒ (re-)arm the proactive timer
        }
    };
    document.addEventListener('visibilitychange', visibilityListener);
}
export function stopVisibilityRefresh() {
    if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
        visibilityListener = null;
    }
}
/** The window of each handler. A `WeakMap`, so a handler nobody holds takes its window with it. */
const maintenanceWindows = new WeakMap();
/** The window of `handler`, made closed and empty on the first use. */
function maintenanceWindowOf(handler) {
    let found = maintenanceWindows.get(handler);
    if (found === undefined) {
        found = { open: false, loops: new Set(), sleepers: new Set() };
        maintenanceWindows.set(handler, found);
    }
    return found;
}
/**
 * The largest delay, in milliseconds, that `setTimeout` keeps: 2^31 - 1. A larger delay fires
 * after 1 ms, so the attempt deadline refuses it.
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;
/**
 * Build a fetch that attaches a bearer, refreshes once on a 401, and absorbs
 * cold starts and maintenance.
 *
 * Every answer of a call goes through `classifyResponse`, the first one and
 * each retry's. So a retry that answers 401 takes the 401 arm, and one that
 * answers a cold start takes the cold-start arm (issue #3699). Per call, the
 * 401 arm runs at most once and the cold-start arm sends at most `maxRetries`
 * retries in all. A 401 after that, or a cold answer after the last retry, goes
 * back to the caller as it is.
 *
 * Each wait between two attempts obeys the caller's signal: the signal of the
 * attempt that answered last, by the Fetch rule. An abort ends the wait at
 * once, the call rejects with the signal's reason, and the closure sends
 * nothing more. A `Retry-After` above 120 s waits 120 s, and the maintenance
 * backoff doubles up to 120 s. The cold-start wait is the larger of
 * `coldStart.backoffMs` and `Retry-After`. A caller's `backoffMs` has no
 * ceiling, so its wait can pass 120 s.
 *
 * The caller headers are `init.headers` when it is not `undefined`, else the
 * headers of a `Request` input, as `fetch` reads them. Every attempt starts
 * from them.
 *
 * A caller `Authorization` other than the closure's own bearer,
 * `Bearer <the closure's token>`, is the caller's own credential, a setup token
 * for example. It goes out unchanged on every attempt, and its retries read no
 * bearer. A caller `Authorization` other than the closure's own bearer gets its
 * 401 back as it is: no refresh, no renew, no replay and no session end. A
 * caller `Authorization` equal to the closure's own bearer counts as that
 * bearer, and the call runs as if the caller had set none.
 *
 * A call whose first attempt carried the closure's bearer stays with the user
 * of that bearer (issue #3589):
 * - A retry after a wait reads the bearer again, so a same-user rotation goes
 *   out with the new token. A bearer of another `sub`, or no bearer, stops the
 *   call, and it returns the last answer it holds. A token with no `sub`
 *   (opaque or malformed) binds by its bytes, so only the same token retries.
 * - Under a `tokenSource`, a retry that reads no bearer calls `renew` once and
 *   goes ahead only with a renewed token of the same `sub`, or the same bytes.
 * - The 401 arm returns the original 401, with no replay, when the refreshed
 *   or renewed token names a `sub` other than the first attempt's. For a first
 *   token with no `sub`, the 401 arm makes no compare with the first attempt.
 *   The renew arm then compares no principal, so a renew of such a token
 *   replays with whatever the renew returns. That is the one residual the
 *   design accepts. The refresh arm still compares the refreshed `sub` with
 *   the `sub` of the session before the 401, for every token.
 * A first attempt with no bearer names no user, and its retries take the
 * bearer the tab holds then.
 *
 * @param maintenance Receives the maintenance window of every 503 that carries
 *   `Retry-After` (see `MaintenanceHandler`). Closures that share the object
 *   share one window: one start, one end, and the first answer other than 503
 *   ends the wait of every loop in it. A maintenance loop has no retry bound; it
 *   waits `Retry-After`, then doubles the wait up to 120 s. Omit the handler
 *   and a maintenance response is returned to the caller as it is.
 * @param coldStart Bounds the cold-start retry loop. Defaults to 4 retries
 *   with `coldStartBackoffMs`.
 * @param tokenSource Where the bearer comes from. Omit it and the closure
 *   behaves as it does today at every one of its four `getAccessToken()`
 *   reads: the initial request, the cold-start retry, the maintenance retry,
 *   and `refreshAndRetry`'s `sessionBefore`. It keeps the whole session path,
 *   the `sub` comparison against the pre-401 session, `onTokenRefreshed`,
 *   `onSessionExpired` and `doRefresh`'s coalescing.
 *
 *   With a `tokenSource`, two things change and nothing else. The three
 *   injection sites take the bearer from `tokenSource.getToken()`. On a 401
 *   the fetch calls `tokenSource.renew(input)` once, passing the very request
 *   that 401'd so the source can classify it (R-041): a non-null result
 *   replays the original request once with that bearer, `null` surfaces the
 *   original 401, and a second 401 after the replay is returned as it is.
 *   That arm compares the renewed `sub` only with the first attempt's, and
 *   it calls neither `onTokenRefreshed` nor `onSessionExpired`, because an app
 *   token's failure is no verdict on the session (`security-audit.md` C4).
 *   The cold-start and maintenance loops keep their bounds, and their retries
 *   compare the `sub` of the source's token as they do for the session's.
 * @param options Bounds the network attempts. With `attemptTimeoutMs`, the
 *   first request, the two 401 replays, the cold-start retry and the
 *   maintenance retry of a request that is safe to send again each join the
 *   caller's signal with a fresh deadline. The caller's signal is the one `fetch` itself obeys: the
 *   `init.signal` of that attempt when it is not `undefined` (`null` means no
 *   signal), else the signal of a `Request` input. A caller signal that is
 *   already aborted rejects the attempt with its reason, and the attempt sends
 *   nothing. The deadline stops when `fetch` resolves, so it never cuts a
 *   response body; the caller's own signal still aborts the body. A request is safe
 *   to send again when `isColdStartRetryable` accepts it: a `GET`, a `HEAD`,
 *   or a request with an `Idempotency-Key`. A deadline miss on its
 *   maintenance retry counts as one failed retry. The maintenance retry of any
 *   other request has no deadline and waits for its answer. Any other
 *   rejection of a maintenance retry reaches the caller as it is. Omit the
 *   option and every attempt is the plain `fetch` call it was before it.
 *
 *   With or without the option, a 401 replay that rejects (a network error,
 *   the deadline, a caller abort) fails the call: the call rejects with that
 *   error and leaves the session alone. `onSessionExpired` runs only when
 *   `doRefresh` itself fails with an error that is not a
 *   `TransientRefreshError`.
 * @returns A `fetch`-shaped closure. It holds `tokenSource` and reads it per
 *   request; it stores nothing itself.
 * @throws `RangeError` at construction when `options.attemptTimeoutMs` is
 *   present and is not a finite number above 0 and at most 2147483647.
 */
export function createAuthFetch(maintenance, coldStart, tokenSource, options) {
    const maxColdStartRetries = coldStart?.maxRetries ?? 4;
    const coldStartBackoff = coldStart?.backoffMs ?? coldStartBackoffMs;
    const attemptTimeoutMs = options?.attemptTimeoutMs;
    if (attemptTimeoutMs !== undefined &&
        !(Number.isFinite(attemptTimeoutMs) && attemptTimeoutMs > 0 && attemptTimeoutMs <= MAX_TIMER_DELAY_MS)) {
        throw new RangeError(`attemptTimeoutMs must be a finite number above 0 and at most ${MAX_TIMER_DELAY_MS}, got ${attemptTimeoutMs}`);
    }
    // The reasons this closure's attempt deadlines aborted with. A caller abort never gives one of
    // these objects, so the maintenance loop tells a missed deadline from a caller abort by identity,
    // whatever `name` the caller's reason has.
    const deadlineReasons = new WeakSet();
    const isAttemptDeadline = (err) => err instanceof DOMException && deadlineReasons.has(err);
    // One network attempt. With no deadline it is the plain `fetch` call. With one, the caller's
    // signal joins a controller whose timer fires `attemptTimeoutMs` after the send. The `finally`
    // clears the timer when `fetch` settles, so the timer bounds the wait for the headers and never
    // the body. The helper uses no `AbortSignal.timeout`, because no code can clear one, and a
    // joined signal then still watches the body. `init` is the copy each site already spread, so a
    // caller's `signal` getter ran once for this attempt, after the site read the bearer.
    async function sendAttempt(input, init) {
        if (attemptTimeoutMs === undefined)
            return fetch(input, init);
        // The caller's signal is the one `fetch` obeys. An `init.signal` other than `undefined`
        // replaces the signal of a `Request` input, and `null` drops it. So the Request's own signal
        // joins only when `init.signal` is `undefined`.
        const callerSignal = callerSignalOf(input, init);
        const deadline = new AbortController();
        const signal = callerSignal ? AbortSignal.any([callerSignal, deadline.signal]) : deadline.signal;
        if (signal.aborted)
            throw signal.reason;
        const timer = setTimeout(() => {
            const reason = new DOMException(`no response headers within ${attemptTimeoutMs} ms`, 'TimeoutError');
            deadlineReasons.add(reason);
            deadline.abort(reason);
        }, attemptTimeoutMs);
        try {
            return await fetch(input, { ...init, signal });
        }
        finally {
            clearTimeout(timer);
        }
    }
    // The bearer for this closure. With no `tokenSource` it is the stored
    // session token, which is the shipped behaviour at all three injection
    // sites; with one, it is whatever the source answers for this request.
    const readToken = () => tokenSource ? tokenSource.getToken() : getAccessToken();
    const classify = (r) => classifyResponse(r.status, r.headers.get('Retry-After'), r.headers.get('x-keda-http-cold-start'));
    async function refreshAndRetry(original, call) {
        // The session the 401 belonged to. `doRefresh` may return a token that a
        // concurrent login or another tab stored (session adoption), and a request
        // authorized as Alice must not be replayed as Bob: for a non-idempotent
        // request that performs Alice's operation under the wrong principal
        // (Copilot, PR #2625). The check is on the principal, `sub`, which the DP
        // mints as `user.id`: a rotation of Alice's own pair changes the token and
        // MUST still replay, which is the whole 401-retry feature. The DP token
        // carries no session id, so a same-user re-login is indistinguishable from
        // a rotation and replays too; the operation still runs as its author.
        // `replayAllowed` adds the user of the first attempt, because a call that waited out
        // maintenance can reach this arm after the session changed. A refresh that names a user
        // other than the first attempt's then gives the original 401.
        const sessionBefore = extractClaims(getAccessToken()).sub;
        let newToken;
        try {
            newToken = await doRefresh();
            onTokenRefreshed?.(newToken);
            if (extractClaims(newToken).sub !== sessionBefore || !call.replayAllowed(newToken)) {
                // Surface the original 401 and do not replay. No `onSessionExpired`:
                // the session now stored is another principal's and is healthy.
                return { kind: 'terminal', res: original };
            }
        }
        catch (err) {
            if (!(err instanceof TransientRefreshError)) {
                onSessionExpired?.(expiredSessionOf(err)); // terminal (or any non-transient) ⇒ end that session
            }
            return { kind: 'terminal', res: original };
        }
        // The replay sits outside the `try` (plan.md section 2 of 2026-10-03-shell-fetch-stream-timeout).
        // The refresh succeeded. So a replay that rejects is a failed request and no verdict on the
        // session. The causes are a network error, the attempt deadline and a caller abort. The replay
        // rejects to the caller, as a rejected first attempt does, and `onSessionExpired` does not run.
        const retryHeaders = call.callerHeaders();
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
        return { kind: 'retried', res: await call.send({ ...call.init, headers: retryHeaders }), token: newToken };
    }
    // 401 under a `tokenSource` (WS-2 §3.2). The source mints a fresh bearer for
    // the request that 401'd and the request replays once with it. `null` means
    // the source could not mint, and the original 401 is what the caller sees.
    // A renewed bearer of a user other than the first attempt's gives the original 401 too, so
    // the replay never runs as another user. This arm calls neither `onTokenRefreshed` nor
    // `onSessionExpired`: an app token's failure is no verdict on the session
    // (`security-audit.md` C4).
    async function renewAndReplay(source, original, call) {
        const renewed = await source.renew(call.input);
        if (renewed === null || !call.replayAllowed(renewed)) {
            return { kind: 'terminal', res: original };
        }
        const retryHeaders = call.callerHeaders();
        retryHeaders.set('Authorization', `Bearer ${renewed}`);
        return { kind: 'retried', res: await call.send({ ...call.init, headers: retryHeaders }), token: renewed };
    }
    /** The one 401 route: `renewAndReplay` under a source, `refreshAndRetry` without one. */
    function handleUnauthorized(original, call) {
        return tokenSource ? renewAndReplay(tokenSource, original, call) : refreshAndRetry(original, call);
    }
    return async (input, init) => {
        // The caller headers, as `fetch` reads them: `init.headers` when it is not `undefined`, else
        // the headers of a `Request` input, so a `Request` keeps its own headers. Each attempt starts
        // from a copy.
        const callerHeaders = () => new Headers(init?.headers !== undefined ? init.headers : input instanceof Request ? input.headers : undefined);
        const token = readToken();
        const ownBearer = token ? `Bearer ${token}` : null;
        const callerAuthorization = callerHeaders().get('Authorization');
        // A caller `Authorization` other than the bearer this closure would attach is foreign: a setup
        // token, or a credential of the caller's own. The call sends it unchanged on every attempt,
        // reads no bearer for a retry, and skips the 401 arm, so a 401 goes back as it is and no
        // session bearer replaces the caller's credential. A caller header equal to that bearer, as
        // the shell's `authedRequest` sends, is the closure's own bearer, and the call runs as if the
        // caller had set none.
        const foreignAuthorization = callerAuthorization !== null && callerAuthorization !== ownBearer;
        const headers = callerHeaders();
        if (ownBearer !== null && !foreignAuthorization) {
            headers.set('Authorization', ownBearer);
        }
        // Issue #3589: the user of the first attempt. A retry whose bearer names another user, or no
        // bearer at all, stops the call. A first attempt with no bearer names no user, so its retries
        // take whatever bearer the tab holds then, as before. The compare is on `sub`, the rule of
        // `refreshAndRetry`, so a rotation of the same user's token still retries. A token with no
        // `sub` (opaque, no claim, malformed) binds by its bytes, so only the same token retries.
        // After a 401 replay the call holds the bytes of the replay's token.
        const boundToUser = ownBearer !== null && !foreignAuthorization;
        const firstSub = extractClaims(token).sub;
        let boundToken = token;
        const isBoundUser = (candidate) => candidate !== null && (firstSub !== null ? extractClaims(candidate).sub === firstSub : candidate === boundToken);
        // The 401 arm compares only a `sub`. A renew or a refresh always gives new bytes, so the byte
        // rule there would stop every replay of a call whose token has no `sub`.
        const replayAllowed = (newToken) => !boundToUser || firstSub === null || extractClaims(newToken).sub === firstSub;
        // The headers of a retry after a wait, or `null` when the bearer now belongs to another user.
        // A `TokenSource` that answers `null` on a bound retry gets one `renew`: the shell's app-token
        // store empties on every session rotation, so `null` there means "mint again". The retry
        // goes ahead only with a renewed token of the same user.
        const retryHeadersOrStop = async () => {
            const retryHeaders = callerHeaders();
            if (foreignAuthorization)
                return retryHeaders;
            let retryToken = readToken();
            if (boundToUser) {
                if (retryToken === null && tokenSource)
                    retryToken = await tokenSource.renew(input);
                if (!isBoundUser(retryToken))
                    return null;
            }
            if (retryToken) {
                retryHeaders.set('Authorization', `Bearer ${retryToken}`);
            }
            return retryHeaders;
        };
        // Each wait obeys the signal of the attempt that answered last. `attemptInit` is the copy the
        // site spread, so a caller's `signal` getter still runs once per attempt.
        const last = { signal: null };
        const send = (attemptInit) => {
            last.signal = callerSignalOf(input, attemptInit);
            return sendAttempt(input, attemptInit);
        };
        // The maintenance loop of this call, in the window of `handler` (see `MaintenanceHandler`).
        // A loop that finds no window open opens it with `onMaintenanceStart`, and a loop that finds
        // it open joins it. A failed retry calls `onMaintenanceRetryFailed`. The first retry answer
        // other than `503` closes the window, wakes every loop that waits and calls `onMaintenanceEnd`.
        // A loop that leaves with no such answer calls `onMaintenanceEnd` only as the last loop of an
        // open window. The loop resolves with the first retry answer that is not a `503`, or with
        // `stopped` and the last answer it holds when the user changed. It rejects as a retry, a wait
        // or a handler callback rejects. It has no bound of its own: it ends when the server answers.
        const waitOutMaintenance = async (handler, answer, retryAfterSec) => {
            // The window of the handler, shared by every closure that holds it.
            const shared = maintenanceWindowOf(handler);
            const loop = {};
            // A missed deadline does not prove that the server skipped the request. So only a request
            // that is safe to send again (the rule of the cold-start arm) gets the attempt deadline
            // here. An unsafe request waits for its answer as it does with no option.
            const safeToResend = isColdStartRetryable(input, init);
            let succeeded = false;
            let delay = retryAfterSec;
            let held = answer;
            try {
                // Inside the `try`, so the `finally` removes the loop and closes the window when
                // `onMaintenanceStart` throws.
                shared.loops.add(loop);
                if (!shared.open) {
                    shared.open = true;
                    handler.onMaintenanceStart(retryAfterSec);
                }
                for (;;) {
                    const wait = startWait(delay * 1000, last.signal);
                    shared.sleepers.add(wait.wake);
                    try {
                        await wait.done;
                    }
                    finally {
                        shared.sleepers.delete(wait.wake);
                    }
                    const retryHeaders = await retryHeadersOrStop();
                    if (retryHeaders === null)
                        return { stopped: true, res: held };
                    const retryInit = { ...init, headers: retryHeaders };
                    // `null` marks a retry that missed its attempt deadline.
                    let retryRes;
                    try {
                        if (safeToResend) {
                            retryRes = await send(retryInit);
                        }
                        else {
                            last.signal = callerSignalOf(input, retryInit);
                            retryRes = await fetch(input, retryInit);
                        }
                    }
                    catch (err) {
                        // A caller abort and a network error reach the caller. The `finally` below leaves the
                        // window.
                        if (!isAttemptDeadline(err))
                            throw err;
                        retryRes = null;
                    }
                    if (retryRes !== null && retryRes.status !== 503) {
                        succeeded = true;
                        if (shared.open) {
                            // The first retry answer other than `503` ends the window and wakes every other
                            // loop that waits. The wake comes first, so an `onMaintenanceEnd` that throws still
                            // wakes them.
                            shared.open = false;
                            for (const wake of shared.sleepers)
                                wake();
                            handler.onMaintenanceEnd();
                        }
                        return { stopped: false, res: retryRes };
                    }
                    if (retryRes !== null)
                        held = retryRes;
                    if (shared.open) {
                        // A 503 and a missed deadline are the same failed retry: the server is still away.
                        handler.onMaintenanceRetryFailed();
                        delay = computeBackoffDelay(delay);
                    }
                    else {
                        // The reopen: another loop closed the window, and this retry found the server away
                        // again. The loop opens a new window. Its wait restarts from the Retry-After of this answer,
                        // or takes the next backoff when the answer has none.
                        const again = retryRes === null ? null : classify(retryRes);
                        delay = again !== null && again.type === 'maintenance' ? again.retryAfterSec : computeBackoffDelay(delay);
                        shared.open = true;
                        handler.onMaintenanceStart(delay);
                    }
                }
            }
            finally {
                shared.loops.delete(loop);
                // A loop that leaves with no answer other than `503` ends the window only as its last
                // loop.
                if (!succeeded && shared.open && shared.loops.size === 0) {
                    shared.open = false;
                    handler.onMaintenanceEnd();
                }
            }
        };
        const unauthorized = { input, init, callerHeaders, replayAllowed, send };
        // The dispatch. Every answer, the first and each retry's, goes through `classify` here, so a
        // maintenance retry that answers 401 or a cold start takes that arm (issue #3699). The bounds,
        // per call:
        // 1. The 401 arm runs at most once, and never for a foreign `Authorization`.
        // 2. The cold-start arm sends at most `maxColdStartRetries` retries in all.
        // 3. The maintenance arm returns only an answer that is not a 503, and such an answer never
        //    classifies as maintenance. So the next turn returns or spends a bound of 1 or 2.
        // A maintenance answer comes only from the first attempt, the 401 replay or a cold-start
        // retry. So the call enters the maintenance loop at most `maxColdStartRetries` plus 2 times,
        // and only the 503 retries inside that loop have no bound, as before this change.
        let res = await send({ ...init, headers });
        let unauthorizedArmRan = false;
        let coldRetries = 0;
        for (;;) {
            const action = classify(res);
            if (action.type === 'retry_with_refresh' && !unauthorizedArmRan && !foreignAuthorization) {
                unauthorizedArmRan = true;
                const outcome = await handleUnauthorized(res, unauthorized);
                if (outcome.kind === 'terminal')
                    return outcome.res;
                boundToken = outcome.token;
                res = outcome.res;
            }
            else if (action.type === 'cold_start_retry' &&
                coldRetries < maxColdStartRetries &&
                // Mutation guard: a keyless non-idempotent request opts out of auto-retry.
                isColdStartRetryable(input, init)) {
                await startWait(Math.max(coldStartBackoff(coldRetries), action.retryAfterSec * 1000), last.signal).done;
                coldRetries += 1;
                const retryHeaders = await retryHeadersOrStop();
                if (retryHeaders === null)
                    return res;
                res = await send({ ...init, headers: retryHeaders });
            }
            else if (action.type === 'maintenance' && maintenance) {
                const exit = await waitOutMaintenance(maintenance, res, action.retryAfterSec);
                if (exit.stopped)
                    return exit.res;
                res = exit.res;
            }
            else {
                // Warm, pass-through, too large, a second 401, a 401 of a foreign `Authorization`, an
                // exhausted or unsafe cold start, or maintenance with no handler: the caller gets the
                // answer as it is.
                return res;
            }
        }
    };
}
// ── Server-side revocation (moved from auth-context.tsx, WS-6 Q4) ───────────
/**
 * Upper bound on the best-effort logout request. `endSession` awaits it while
 * holding the refresh mutex, so a request that stalls without rejecting would
 * pin the lock and queue every later refresh behind it until a reload
 * (Copilot, PR #2625). The local clear does not depend on the server's answer,
 * so a timed-out request costs only the server-side revocation, which the
 * next refresh with that pair also triggers.
 */
export const LOGOUT_REQUEST_TIMEOUT_MS = 10_000;
/**
 * How long `revokeServerSession` waits before it repeats a request an N-1 core-api pod answered 400 (R-48).
 *
 * One second, because the ingress load-balances per request. The next request draws an endpoint
 * again, so it can land on a pod of the other version. This delay is the roll's pod-swap
 * granularity rather than a backoff on a busy server, so it stays short enough that the user's
 * sign-out click still settles while the click feels live.
 */
export const LOGOUT_RETRY_DELAY_MS = 1_000;
/**
 * How many logout requests one sign-out sends at most, counting the first (R-48).
 *
 * Three. The mixed-roll window is seconds long with `coreApi.replicas: 1`, so three draws from the
 * ingress cover it. A 400 comes from a pod that answered, so each repeated request returns
 * promptly and the two delays add 2 seconds to the click. A request that stalls instead hits
 * `LOGOUT_REQUEST_TIMEOUT_MS` and rejects, which is not a 400 and ends the sequence at one.
 */
export const LOGOUT_ATTEMPTS = 3;
/**
 * Revoke the session server-side, best effort. Moved unchanged from `auth-context.tsx`, where it
 * was the private `apiLogout`, so the shell loader (layered shell surfaces, WS-6 Q4) signs out
 * through the same request `AuthProvider` sends; both import it from here. The design named it
 * `revokeSession`; that word is a deleted shell client function the shell's admin-extraction
 * census bans, so the export carries the longer name.
 *
 * It posts `/v1/auth/logout` with `Content-Type: application/json`, `Authorization: Bearer
 * <token>` and `sessionTransportHeaders()`. Under the transport header core-api takes the
 * credential from the cookie and clears both `zion_refresh_token` and `zion_access_token` (D7,
 * chain C4 step 1). Drop the header and core-api takes the body arm, finds no token, and clears
 * no cookie.
 *
 * ⛔ THE BODY CARRIES THE LEGACY TOKEN WHEN THE TAB IS ON THE BODY ARM (R-33). A tab an N-1
 * core-api pod put back on the body arm holds its credential in `legacy-storage.ts` and has no
 * cookie, so `{}` would leave `selectRefreshCredential` with nothing, `AuthService.logout` would
 * not run, and the session row would live out its 30-day sliding expiry. The pre-cookie client
 * read the token out of `localStorage` and sent it; sending it here keeps that revocation
 * through the mixed-roll window. Every other tab sends `{}`, because script cannot read the
 * cookie and the cookie is the credential.
 *
 * ⛔ A 400 UNDER THE COOKIE ARM IS AN N-1 POD, AND THE REQUEST REPEATS (R-48). A current pod
 * takes the credential from the cookie and answers this request 200 or 401. A pod that predates
 * D7 requires a `refreshToken` in the body and rejects `{}` with a 400, so a 400 with no legacy
 * token identifies it. Returning there clears the tab while the family and the cookie live on,
 * and the next page load hydrates the session back. So that one case waits
 * `LOGOUT_RETRY_DELAY_MS` and posts the same request again, `LOGOUT_ATTEMPTS` requests in all.
 * Revoking a family the server already revoked is a no-op, so a repeat costs nothing when the
 * first request did land. Every other status, and the body arm, return after one request.
 *
 * @param token the access token this session's bearer is built from.
 * @returns nothing. The status is read for the one case above and the body is never read. The
 *   caller swallows a rejection, because the local clear does not depend on the server's answer.
 */
export async function revokeServerSession(token) {
    const legacyRefreshToken = getLegacyRefreshToken();
    const body = JSON.stringify(legacyRefreshToken === null ? {} : { refreshToken: legacyRefreshToken });
    for (let attempt = 1;; attempt += 1) {
        const res = await fetch('/v1/auth/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...sessionTransportHeaders(),
            },
            body,
            signal: AbortSignal.timeout(LOGOUT_REQUEST_TIMEOUT_MS),
        });
        if (res.status !== 400 || legacyRefreshToken !== null || attempt === LOGOUT_ATTEMPTS)
            return;
        await new Promise((resolve) => setTimeout(resolve, LOGOUT_RETRY_DELAY_MS));
    }
}
//# sourceMappingURL=token-manager.js.map